/**
 * Theme Service
 *
 * Handles fetching and caching of tenant theme configuration
 */
import { apiClient } from '@/shared/utils/api';
import { Theme, ThemeResponse } from '@/types/theme';
import { BACKEND_CONFIG } from '@/config/backendConfig';

/**
 * Get admin auth token from session storage
 */
const getAdminAuthHeader = () => {
  const token = localStorage.getItem('admin_access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Fetch current tenant theme from API
 */
export const fetchCurrentTenantTheme = async (): Promise<ThemeResponse> => {
  const endpoint = BACKEND_CONFIG.isFastAPI ? '/api/theme/' : '/admin/theme/';
  const response = await apiClient.get<ThemeResponse>(endpoint);
  return response.data;
};

/**
 * Fetch theme for specific tenant by ID (requires admin auth)
 */
export const fetchTenantThemeById = async (tenantId: number): Promise<ThemeResponse> => {
  const response = await apiClient.get<ThemeResponse>(
    `/admin/tenants/${tenantId}/theme/`,
    { headers: getAdminAuthHeader() }
  );
  return response.data;
};

/**
 * Update tenant theme (requires admin auth)
 */
export const updateTenantTheme = async (
  tenantId: number,
  themeUpdates: Partial<Theme>
): Promise<ThemeResponse> => {
  const response = await apiClient.patch<ThemeResponse>(
    `/admin/tenants/${tenantId}/theme/update/`,
    themeUpdates,
    { headers: getAdminAuthHeader() }
  );
  return response.data;
};

/**
 * Invalidate theme cache (for debugging)
 */
export const invalidateThemeCache = async (tenantId: number): Promise<void> => {
  await apiClient.post(`/admin/tenants/${tenantId}/theme/invalidate/`);
};
